import { LogoutOutlined, SettingOutlined, UserOutlined, DownOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {USER_KEY} from '@/utils/constants';
import TeamDropdown from '../TeamDropdown';
import { ReactSVG } from 'react-svg';
import { Tooltip } from 'antd';


export const SelectVersion = () => {
  const [selectedTeam, setSelectedTeam] = useState({});
  const [isOverflow, setIsOverflow] = useState(false); // 检测文字是否溢出
  const textRef = useRef<HTMLSpanElement>(null); // 引用文字容器:用于显示溢出文字
  const [teamMenuList, setMenuTeamList] = useState([]); // 团队/租户列表

  const { initialState, setInitialState } = useModel('@@initialState');

  // const currentUser = initialState?.currentUser; // 获取当前用户信息
  const currentTenantId = initialState?.currentTenantId;
  /**
   * 
   * 
   * @returns 自定义个人空间item数据
   * 当选中个人空间时roles中增加person权限
   * 当选中团队时也要将person权限删除
   */
  const menuPersonItems = () => {
    return {
      id: 0,
      key: '0',
      icon: <ReactSVG src="./icons/icon-personalSpace.svg" />,
      label: '个人空间',
    };
  }

  // 使用 useCallback 避免函数重复创建
  const addNewTenantToList = useCallback((newTenantInfo) => {
    if (!newTenantInfo || !newTenantInfo.id) {
      console.error('无效的租户信息');
      return;
    }

    setInitialState((prevState) => {
      // 现有的租户列表
      const existingTeamList = prevState.currentUser?.teamList || [];
      // 检查是否已存在相同的租户 ID
      const isAlreadyInList = existingTeamList.some((team) => team.id === newTenantInfo.id);

      if (isAlreadyInList) {
        console.warn('租户已存在于团队列表中:', newTenantInfo);
        return prevState; // 如果租户已存在，直接返回当前状态
      }

      const updatedUser = {
        ...prevState.currentUser,
        teamList: [
          ...existingTeamList,
          {
            id: newTenantInfo.id,
            name: newTenantInfo.name,
            roles: newTenantInfo.roles || [],
          },
        ],
      };

      // 更新 localStorage
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));

      // 返回新的状态
      return {
        ...prevState,
        currentUser: updatedUser,
      };
    });
  }, []);

  // 页面加载时读取团队列表
  useEffect(() => {
    getTeamList();
  }, [currentTenantId, initialState?.currentUser]);

  // 页面加载时根据当前租户ID选择个人空间还是租户
  useEffect(() => {
    if (currentTenantId !== null && currentTenantId !== undefined) {
      onMenuClick({ key: currentTenantId + '' }, false);
    }
  }, [teamMenuList, currentTenantId]);

  useEffect(() => {
    if (initialState?.currentTenantId == null) {
      const stored = localStorage.getItem(USER_KEY);
      if (stored) {
        try {
          const { currentTenantId: storedTid } = JSON.parse(stored);
          if (storedTid != null) {
            setInitialState(prev => ({
              ...prev,
              currentTenantId: storedTid,
            }));
          }
        } catch (error) {
          console.error('恢复 currentTenantId 失败', error);
        }
      }
    }
  }, []); // 仅在组件挂载时执行


  // 获取租户信息
  /**
   * 获取团队列表+个人空间，如果有当前团队slect当前团队列表，如果没有默认select个人空间
   */
  const getTeamList = () => {
    // 初始化团队列表
    const teamList: object[] = [];
    let currentTeamList = null;

    const latestCurrentUser = initialState?.currentUser;

    // 检查 currentUser?.teamList 是否为数组
    if (!Array.isArray(initialState?.currentUser?.teamList)) {
      console.error('currentUser.teamList 不是有效的数组');
      return;
    }

    // 遍历团队列表
    latestCurrentUser?.teamList?.forEach((item) => {

      if (!item || !item.id || !item.name) {
        console.warn('团队信息缺失:', item);
        return; // 跳过无效的团队项
      }

      //如果只有租户管理员，则不显示 列表
      if (item.roles.includes('tenant_admin') && item.roles.length == 1) {
        return;
      }

      teamList.push({
        id: item.id,
        key: String(item.id), // 确保 key 是字符串
        icon: <ReactSVG src="./icons/icon-team.svg" />,
        label: item.name,
      });
    });

    // 添加个人空间到团队列表
    teamList.push(menuPersonItems());

    // 根据 currentTeamId 查找当前团队
    if (currentTenantId) {
      currentTeamList = teamList.find((item) => item.key === String(currentTenantId));
      if (!currentTeamList) {
        console.warn('未找到匹配的团队:', currentTenantId);
      }
    }
    // 设置默认选中团队：如果没有默认选择个人空间
    if (currentTeamList) {
      setSelectedTeam(currentTeamList);
    } else {
      setSelectedTeam(menuPersonItems());
    }
    // 更新团队列表
    setMenuTeamList(teamList);
  }


  useEffect(() => {
    // 检测文字是否超出容器宽度
    if (textRef.current) {
      const { scrollWidth, offsetWidth } = textRef.current;
      setIsOverflow(scrollWidth > offsetWidth); // 如果内容宽度大于容器宽度，则溢出
    }
  }, [selectedTeam?.label]); // 当选中团队的 label 变化时重新检测

  // 处理选中的团队
  /**
   * 获取选中团队的Id更新currentUser中的currentTeamId字段
   * 更新roles字段：根据baseRoles和当前选择团队的roles
   * 当切换团队时，key ！== 0时要删除person权限
   * shouldNavigate true:表示是从菜单点击进入该方法， false表示是初始化时调用该方法
   * @param event 刷新菜单
   * @returns 
   */
  const onMenuClick = (event, shouldNavigate = true) => {
    const { key } = event;
    // if (key === 'createTeam' || key === 'joinTeam') {
    //   return; // 不更新 selectedKeys
    // }

    // 获取选中项，包括个人空间
    const selectedItem: object = teamMenuList.find((item) => item.key === key) || {};
    if (!selectedItem) {
      return;
    }
    // 获取当前用户信息
    const currentUserInfo = initialState?.currentUser || {};

    // 根据选中的租户获取对应的 roles
    let currentRoles = [];
    if (Number(key) === 0) {
      // 选中个人空间，设置为 person 权限
      currentRoles = ['person']; // 或者根据你的业务逻辑设置个人空间的权限
    } else {
      // 选中租户，从 teamList 中获取对应的 roles
      const selectedTenant = currentUserInfo?.teamList?.find(team => team.id === Number(key));
      currentRoles = selectedTenant?.roles || [];
    }

    //增加租户管理员
    const tenantAdmin = 'tenant_admin';
    if (!currentRoles.includes(tenantAdmin)) {
      if (!Array.isArray(currentUserInfo?.teamList)) {
        console.warn('teamList 不是有效的数组:', currentUserInfo?.teamList);
        return;
      }
      for (let i = 0; i < currentUserInfo?.teamList.length; i++) {
        const tenantRole = currentUserInfo?.teamList[i];
        if (tenantRole.roles.includes(tenantAdmin)) {
          currentRoles.push(tenantAdmin);
          break;
        }
      }
    }

    // 创建更新后的用户信息
    const updatedUserInfo = {
      ...currentUserInfo,
      currentTenantId: shouldNavigate ? Number(key) : currentTenantId,  // 更新 currentTenantId
      roles: currentRoles,
    };
    // 更新 localStorage 中的用户信息
    // 更新全局状态中的 currentTenantId
    setInitialState((prevState) => ({
      ...prevState,
      currentTenantId: shouldNavigate ? Number(key) : currentTenantId,
      currentUser: updatedUserInfo,  // 同时更新 currentUser
      roles: currentRoles,
    }));
    setSelectedTeam(selectedItem);
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUserInfo));
    // 跳转逻辑
    if (shouldNavigate) {
      // 如果是个人空间，可以跳转到个人空间首页
      if (window.location.pathname !== '/dashboard') {
        history.push('/dashboard');
      }
    }
  };

  return (
    <div className="selectVersion">
      <TeamDropdown
        menu={{
          onClick: (event) => onMenuClick(event, true),
          items: teamMenuList,
        }}
        addNewTenantToList={addNewTenantToList}
      >
        <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px',  minWidth: '180px', justifyContent: 'space-around' }}>
          {selectedTeam?.icon && (
            <span style={{ marginRight: '8px', marginLeft: '8px' }}>{selectedTeam?.icon}</span>
          )}
          <Tooltip title={isOverflow ? selectedTeam?.label : null} placement="top">
            <span
              ref={textRef} // 绑定文字容器的引用
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: '120px', // 设置最大宽度
                display: 'inline-block',
                flex: 1,
              }}
            >
              {selectedTeam?.label}
            </span>
          </Tooltip>
          <DownOutlined style={{ marginLeft: '8px' }} />
        </div>
      </TeamDropdown>
    </div>
  );
};